import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MessageCircle } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { PRODUCTS, CATEGORIES, priceLabel, waLink } from "@/data/site";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const ProductCard = ({ p, onOpen }) => (
  <button
    onClick={() => onOpen(p)}
    data-testid={`product-card-${p.id}`}
    className="card-surface group relative overflow-hidden rounded-[1.4rem] text-left transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl"
  >
    <div className="relative overflow-hidden">
      <img
        src={p.image}
        alt={`${p.title} — ${p.keyword}`}
        loading="lazy"
        className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
      />
      <span
        className="absolute left-4 top-4 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider"
        style={{ background: "linear-gradient(90deg,#8B5CF6,#EF4444)", color: "#fff" }}
      >
        {p.tag}
      </span>
    </div>
    <div className="p-5">
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-muted">{p.category}</p>
      <h3 className="mt-2 font-serif text-lg font-semibold leading-snug text-brand-ink">{p.title}</h3>
      <p className="mt-2 text-xs text-brand-muted">{p.specs}</p>
      <p className="mt-3 font-serif text-lg font-bold leading-snug" style={{ color: "var(--t-accent)" }} data-testid={`product-price-${p.id}`}>
        {priceLabel(p.id)}
      </p>
      <span
        className="mt-4 inline-block rounded-full border px-3 py-1 text-[10px] font-medium"
        style={{ borderColor: "color-mix(in srgb, var(--t-accent) 45%, transparent)", color: "var(--t-accent)" }}
      >
        {p.keyword}
      </span>
    </div>
  </button>
);

const ProductModal = ({ p, onClose }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-50 flex items-center justify-center p-4"
    style={{ backgroundColor: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)" }}
    onClick={onClose}
    data-testid="product-modal"
  >
    <motion.div
      initial={{ opacity: 0, y: 60, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 40, scale: 0.97 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      onClick={(e) => e.stopPropagation()}
      className="card-surface grid max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-[1.6rem] md:grid-cols-2"
    >
      <div className="relative">
        <img src={p.image} alt={p.title} className="h-64 w-full object-cover md:h-full" />
        <span
          className="absolute left-4 top-4 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider"
          style={{ background: "linear-gradient(90deg,#8B5CF6,#EF4444)", color: "#fff" }}
        >
          {p.tag}
        </span>
      </div>
      <div className="relative p-7">
        <button
          onClick={onClose}
          aria-label="Tutup"
          data-testid="product-modal-close"
          className="absolute right-5 top-5 rounded-full p-2 text-brand-muted transition-colors hover:text-brand-ink"
        >
          <X size={20} />
        </button>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-muted">{p.category}</p>
        <h3 className="mt-2 pr-8 font-serif text-2xl font-semibold text-brand-ink">{p.title}</h3>
        <p className="mt-3 text-sm font-semibold accent-text">{p.specs}</p>
        <p className="mt-4 font-serif text-2xl font-bold leading-snug" style={{ color: "var(--t-accent)" }}>
          {priceLabel(p.id)}
        </p>
        <p className="mt-1 text-[11px] text-brand-muted">Harga indikatif — harga grosir terbaru via WhatsApp</p>
        <p className="mt-4 text-sm leading-relaxed text-brand-muted">{p.description}</p>
        <span
          className="mt-5 inline-block rounded-full border px-3 py-1 text-[10px] font-medium"
          style={{ borderColor: "color-mix(in srgb, var(--t-accent) 45%, transparent)", color: "var(--t-accent)" }}
        >
          {p.keyword}
        </span>
        <a
          href={waLink(`Halo PT. SINAR AL MUBAROKAH, saya tertarik dengan produk "${p.title}" (${p.specs}). Mohon info harga & ketersediaan stok.`)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="product-modal-whatsapp"
          className="mt-6 flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold transition-transform duration-300 hover:-translate-y-0.5"
          style={{ background: "linear-gradient(90deg,#8B5CF6,#EF4444)", color: "#fff" }}
        >
          <MessageCircle size={16} /> Tanya Harga Produk Ini
        </a>
      </div>
    </motion.div>
  </motion.div>
);

export const Catalog = () => {
  const [filter, setFilter] = useState("Semua");
  const [active, setActive] = useState(null);
  const list = filter === "Semua" ? PRODUCTS : PRODUCTS.filter((p) => p.category === filter);

  return (
    <section id="katalog" data-testid="catalog-section" className="surface py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <SectionHead
          overline="Katalog Produk"
          title={<>20 Produk Kayu & Triplek <span className="italic accent-text">Unggulan</span></>}
          sub="Klik produk untuk detail spesifikasi dan langsung tanya harga via WhatsApp. Semua stok ready di gudang."
        />
        <Reveal className="mb-10 flex flex-wrap gap-3" data-testid="catalog-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={`filter-${slug(c)}`}
              className="rounded-full border px-5 py-2.5 text-xs font-semibold tracking-wide transition-all duration-300"
              style={
                filter === c
                  ? { background: "linear-gradient(90deg,#8B5CF6,#EF4444)", color: "#fff", borderColor: "transparent" }
                  : { borderColor: "var(--t-border)", color: "var(--t-muted)" }
              }
            >
              {c}
            </button>
          ))}
        </Reveal>

        <motion.div layout className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="catalog-grid">
          <AnimatePresence mode="popLayout">
            {list.map((p) => (
              <motion.div
                key={p.id}
                layout
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.94 }}
                transition={{ duration: 0.35 }}
              >
                <ProductCard p={p} onOpen={setActive} />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>{active && <ProductModal p={active} onClose={() => setActive(null)} />}</AnimatePresence>
    </section>
  );
};
